import React from 'react';
import PropTypes from 'prop-types';
import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, Row, Col, Button, Radio, Tooltip, Alert } from 'antd';
import modal from '~components/modal';
import * as queries from '~settings/queries';
import { fetch } from '~helpers/graphql';
import PhoneNumber from '~components/phone-number';
import { validateEmojiRegex, validateEmail } from 'common/helpers/validate';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import { landlordContactType } from '~constants/landlord';

@modal(
  {
    className: 'landlord-contact-modal',
  },
  true,
)
class LandlordContactModal extends React.Component {
  constructor(props) {
    super(props);

    const { currentLandlordContact } = props;
    const { contactName, contactEmail, contactPhone, occupation, isBillingNotificationEmail } =
      currentLandlordContact || {};

    this.state = {
      contactName: contactName || '',
      contactEmail: contactEmail || '',
      contactPhone: contactPhone || '',
      occupation: occupation || '',
      isBillingNotificationEmail: isBillingNotificationEmail || false,

      isNormalEmailOrPhone: true,
      isPhoneMLimit: true,
      isEmailValid: true,

      phoneErrorMsg: null,
      emailErrorMsg: null,
    };

    this.loading = false;
  }

  handleClickConfirm = () => {
    const { type, contactType } = this.props;
    const { contactEmail, contactPhone, isEmailValid } = this.state;
    if (this.loading) {
      return false;
    }

    this.props.form.validateFieldsAndScroll(
      // eslint-disable-next-line consistent-return
      (err, values) => {
        if (contactType === landlordContactType.FINANCE_TEAM) {
          if (!contactEmail) {
            this.setState({
              isEmailValid: false,
              emailErrorMsg: this.props.t('cms.landlord.contact.modal.error.blank'),
            });
            return false;
          }
        } else {
          if (!contactEmail && !contactPhone) {
            this.setState({
              isNormalEmailOrPhone: false,
              phoneErrorMsg: this.props.t(
                'cms.landlord.contact.modal.error.contact_email_phone.limit',
              ),
              emailErrorMsg: this.props.t(
                'cms.landlord.contact.modal.error.contact_email_phone.limit',
              ),
            });
            return false;
          }
        }

        if (contactEmail && !isEmailValid) {
          this.setState({
            isEmailValid: false,
            emailErrorMsg: this.props.t(
              'cms.landlord.contact.modal.error.contact_email_format.error',
            ),
          });
          return false;
        }

        if (values.isBillingNotificationEmail) {
          if (!contactEmail || !validateEmail(contactEmail)) {
            this.setState({
              emailErrorMsg: this.props.t('cms.auth.login.username.placeholder'),
            });
            return false;
          }
        }

        if (!this.validatePhoneAndEmail()) {
          return false;
        }

        if (!err) {
          const params = this.props.form.getFieldsValue();
          params.contactPhone = this.state.contactPhone;
          params.contactEmail = this.state.contactEmail;

          Object.keys(params).map(key => {
            if (typeof params[key] === 'string') {
              params[key] = params[key].trim();
            }
            return true;
          });

          this.loading = true;

          if (!params.contactEmail) {
            params.contactEmail = null;
          }

          if (!params.contactPhone) {
            params.contactPhone = null;
          }

          if (type === 'create') {
            if (!params.occupation) {
              delete params.occupation;
            }
            params.landlordId = this.props.landlord.id;
            params.type = contactType;
            fetch(queries.createLandlordLinkMan(params))
              .then(response => {
                if (
                  response &&
                  response.createLandlordLinkMan &&
                  response.createLandlordLinkMan.landlordLinkMan
                ) {
                  this.props.handleUpdateLandlordContact(
                    'create',
                    response.createLandlordLinkMan.landlordLinkMan,
                  );
                  this.props.handleCloseModal();
                }
              })
              .finally(() => {
                this.loading = false;
              });
          }

          if (type === 'edit') {
            params.id = this.props.currentLandlordContact.id;
            fetch(queries.updateLandlordLinkMan(params))
              .then(response => {
                if (
                  response &&
                  response.updateLandlordLinkMan &&
                  response.updateLandlordLinkMan.landlordLinkMan
                ) {
                  this.props.handleUpdateLandlordContact(
                    'edit',
                    response.updateLandlordLinkMan.landlordLinkMan,
                  );
                  this.props.handleCloseModal();
                }
              })
              .finally(() => {
                this.loading = false;
              });
          }
        }
      },
    );
    return true;
  };

  validatePhoneAndEmail = () => {
    const { contactPhone } = this.state;
    const params = this.props.form.getFieldsValue();
    if (
      this.props.contactType !== landlordContactType.FINANCE_TEAM &&
      params.contactEmail === '' &&
      contactPhone.trim().length <= 3
    ) {
      this.setState({
        isNormalEmailOrPhone: false,
        phoneErrorMsg: this.props.t('cms.landlord.contact.modal.error.blank'),
        emailErrorMsg: this.props.t('cms.landlord.contact.modal.error.blank'),
      });
      return false;
    }
    if (contactPhone.trim().length > 32) {
      this.setState({
        isPhoneMLimit: false,
        phoneErrorMsg: this.props.t('cms.landlord.contact.modal.error.phone.limit'),
      });
      return false;
    }

    if (contactPhone.trim().length > 3 && !isValidPhoneNumber(contactPhone)) {
      this.setState({
        isPhoneMLimit: false,
        phoneErrorMsg: this.props.t('cms.landlord.contact.modal.error.contact_phone.invalid'),
      });
      return false;
    }

    this.setState({
      isNormalEmailOrPhone: true,
      isPhoneMLimit: true,
      phoneErrorMsg: null,
      emailErrorMsg: null,
    });
    return true;
  };

  handleValidateEmail = () => {
    const { t } = this.props;
    const { contactEmail } = this.state;
    if (contactEmail.length > 255) {
      this.setState({
        isEmailValid: false,
        emailErrorMsg: t('cms.landlord.contact.modal.error.contact_email.limit'),
      });
    } else if (!validateEmail(contactEmail)) {
      this.setState({
        isEmailValid: false,
        emailErrorMsg: t('cms.landlord.contact.modal.error.contact_email_format.error'),
      });
    } else {
      this.setState({
        isEmailValid: true,
        emailErrorMsg: null,
        isNormalEmailOrPhone: true,
        phoneErrorMsg: null,
      });
    }
  };

  handleInputEmailChange = e => {
    this.setState({
      contactEmail: e.trim(),
    });
  };

  handleContactPhoneChange = e => {
    if (e && e.trim().length > 4) {
      this.setState({ contactPhone: e });
    } else {
      this.setState({ contactPhone: '' });
    }
  };

  getPhoneValidateStatus = (isNormalEmailOrPhone, isPhoneMLimit) => {
    if (this.props.contactType !== landlordContactType.FINANCE_TEAM) {
      return isNormalEmailOrPhone && isPhoneMLimit ? null : 'error';
    } else {
      return isPhoneMLimit ? null : 'error';
    }
  };

  render() {
    const { t, type, form, landlord } = this.props;
    const { getFieldDecorator } = form;
    const {
      contactName,
      contactEmail,
      contactPhone,
      occupation,
      isBillingNotificationEmail,
      isNormalEmailOrPhone,
      isPhoneMLimit,
      phoneErrorMsg,
      emailErrorMsg,
    } = this.state;

    return (
      <div className="landlord-contact-info">
        <div className="landlord-contact-info__header">
          {t(
            `cms.landlord.contact.modal.${type}_${this.props.contactType.toLowerCase()}_contact.title`,
          )}
          <CloseOutlined
            className="landlord-contact-info__close-button"
            onClick={this.props.handleCloseModal}
          />
        </div>
        <div className="landlord-contact-info__body">
          <If
            condition={[
              landlordContactType.PROPERTY,
              landlordContactType.BOOKING_TEAM,
              landlordContactType.FINANCE_TEAM,
            ].includes(this.props.contactType)}
          >
            <Alert
              className="landlord-contact-info__alert"
              showIcon
              type="info"
              message={t(
                `cms.landlord.contact.modal.alert.${this.props.contactType.toLowerCase()}_contact`,
              )}
            />
          </If>
          <Form className="landlord-contact-info__form">
            <section className="landlord-contact-info__section">
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={`${t('cms.landlord.contact.contact_name.label')}:`}
                    colon={false}
                  >
                    {getFieldDecorator('contactName', {
                      rules: [
                        {
                          required: true,
                          message: t('cms.landlord.contact.modal.error.blank'),
                        },
                        {
                          max: 80,
                          message: t('cms.landlord.contact.modal.error.contact_name.limit'),
                        },
                        {
                          validator: (rule, value, callback) => {
                            if (!validateEmojiRegex(value.trim())) {
                              callback(
                                t('cms.landlord.contact.modal.error.contact_name.emoji.limit'),
                              );
                            } else {
                              callback();
                            }
                          },
                        },
                      ],
                      initialValue: contactName,
                      validateTrigger: 'onBlur',
                    })(<Input placeholder={t('cms.landlord.contact.contact_name.label')} />)}
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label={`${t('cms.landlord.contact.contact_email.label')}:`}
                    colon={false}
                    validateStatus={emailErrorMsg ? 'error' : null}
                    help={emailErrorMsg}
                  >
                    <Input
                      placeholder={t('cms.landlord.contact.contact_email.label')}
                      value={contactEmail}
                      onChange={e => {
                        this.handleInputEmailChange(e.target.value);
                      }}
                      onBlur={this.handleValidateEmail}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={`${t('cms.landlord.contact.contact_phone.label')}:`}
                    colon={false}
                    validateStatus={this.getPhoneValidateStatus(
                      isNormalEmailOrPhone,
                      isPhoneMLimit,
                    )}
                    help={phoneErrorMsg}
                  >
                    <PhoneNumber
                      placeholder={t('cms.properties.edit.others.phone_number')}
                      value={contactPhone}
                      onChange={e => {
                        this.handleContactPhoneChange(e);
                      }}
                      checkValid={() => {
                        this.validatePhoneAndEmail();
                      }}
                      t={t}
                      countrySlug={landlord.billingCountry}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={`${t('cms.landlord.contact.contact_occupation.label')}:`}
                    colon={false}
                  >
                    {getFieldDecorator('occupation', {
                      rules: [
                        {
                          max: 255,
                          message: t('cms.landlord.contact.modal.error.contact_email.limit'),
                        },
                      ],
                      initialValue: occupation,
                      validateTrigger: 'onBlur',
                    })(<Input placeholder={t('cms.landlord.contact.contact_occupation.label')} />)}
                  </Form.Item>
                </Col>
              </Row>

              <If condition={this.props.contactType === 'LANDLORD'}>
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label={
                        <span>
                          {`${t('cms.landlord.contact.send_billing_notification_emails.label')}:`}

                          <Tooltip
                            title={t(
                              'cms.landlord.contact.send_billing_notification_emails.label.tooltip',
                            )}
                          >
                            <QuestionCircleOutlined
                              style={{ marginLeft: 4, color: 'rgba(0, 0, 0, .45)' }}
                            />
                          </Tooltip>
                        </span>
                      }
                      colon={false}
                    >
                      {getFieldDecorator('isBillingNotificationEmail', {
                        rules: [
                          {
                            required: true,
                            message: t('cms.listing.modal.error_message.can_not_empty'),
                          },
                        ],
                        initialValue: isBillingNotificationEmail,
                      })(
                        <Radio.Group>
                          <Radio value={true}>{t('cms.form.value.yes')}</Radio>
                          <Radio value={false}>{t('cms.form.value.no')}</Radio>
                        </Radio.Group>,
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </If>
            </section>
          </Form>
        </div>

        <div className="landlord-contact-info__footer">
          <Button
            ghost
            type="primary"
            size="large"
            className="landlord-contact-info__btn landlord-contact-info__cancel-button"
            onClick={this.props.handleCloseModal}
          >
            {t('cms.landlord.modal.cancel.button')}
          </Button>
          <Button
            type="primary"
            size="large"
            className="landlord-contact-info__btn landlord-contact-info__confirm-button"
            onClick={this.handleClickConfirm}
            loading={this.loading}
          >
            {t('cms.landlord.modal.confirm.button')}
          </Button>
        </div>
      </div>
    );
  }
}

LandlordContactModal.propTypes = {
  t: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['create', 'edit']),
  contactType: PropTypes.oneOf([
    'LANDLORD',
    'BOOKING_TEAM',
    'PROPERTY',
    landlordContactType.FINANCE_TEAM,
  ]),
  landlord: PropTypes.object,
  handleCloseModal: PropTypes.func.isRequired,
  form: PropTypes.shape({
    getFieldDecorator: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
    validateFieldsAndScroll: PropTypes.func.isRequired,
    getFieldsValue: PropTypes.func.isRequired,
  }).isRequired,
  currentLandlordContact: PropTypes.object,
  handleUpdateLandlordContact: PropTypes.func,
};

LandlordContactModal.defaultProps = {
  t: () => {},
  type: 'create',
  contactType: 'LANDLORD',
  landlord: {},
  handleCloseModal: () => {},
  form: {
    getFieldDecorator: () => {},
    setFieldsValue: () => {},
    validateFieldsAndScroll: () => {},
    getFieldsValue: () => {},
  },
  currentLandlordContact: {},
  handleUpdateLandlordContact: () => {},
};

export default Form.create({
  name: 'cms_landlord_modal',
})(LandlordContactModal);
